/**
 * API Service
 * Base API service using AWS Amplify for backend communication
 */

import { get, post, patch, del } from "aws-amplify/api";
import currentUserToken from "../CurrentUserToken";

// API configuration constants
export const API_CONFIG = {
  name: "checklistAPI",
  endpoints: {
    templates: "/templates",
  },
};

/**
 * Base API service class
 */
export class ApiService {
  constructor() {
    this.apiName = API_CONFIG.name;
  }

  /**
   * Makes a GET request to the API
   * @param {string} path - API endpoint path
   * @param {Object} options - Request options
   * @returns {Promise<Object>} API response
   */
  async get(path, options = {}) {
    try {
      const response = await get({
        apiName: this.apiName,
        path,
        options: {
          headers: {
            "Content-Type": "application/json",
            // Authorization: `${await currentUserToken()}`,
            ...options.headers,
          },
          queryParams: options.params || {},
          ...options,
        },
      });

      const { statusCode, body } = await response.response; //attendo la risposta effettiva
      if (statusCode !== 200) {
        //controllo lo status della risposta
        throw new Error(`API Error: status ${statusCode}`);
      }
      const data = await body.json();

      return {
        statusCode: statusCode,
        data: data,
        timestamp: new Date().toISOString(),
      };

      // return this.handleResponse(response.response);
    } catch (error) {
      throw this.handleError(error, "GET", path);
    }
  }

  /**
   * Makes a POST request to the API
   * Used for creating new resources (templates, checklists, etc.)
   * @param {string} path - API endpoint path
   * @param {Object} data - Request body data
   * @param {Object} options - Request options
   * @returns {Promise<Object>} API response
   */
  async post(path, data = {}, options = {}) {
    try {
      const response = await post({
        apiName: this.apiName,
        path,
        options: {
          headers: {
            "Content-Type": "application/json",
            // Authorization: `${await currentUserToken()}`,
            ...options.headers,
          },
          body: data,
          ...options,
        },
      });

      const { statusCode, body } = await response.response; //attendo la risposta effettiva
      if (statusCode !== 200) {
        //controllo lo status della risposta
        throw new Error(`API Error: status ${statusCode}`);
      }
      const responseData = await body.json();

      return {
        statusCode: statusCode,
        data: responseData,
        timestamp: new Date().toISOString(),
      };

      // return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error, "POST", path);
    }
  }

  /**
   * Makes a PATCH request to the API
   * Used for partial updates to existing resources
   * @param {string} path - API endpoint path
   * @param {Object} data - Request body data (partial update)
   * @param {Object} options - Request options
   * @returns {Promise<Object>} API response
   */
  async patch(path, data = {}, options = {}) {
    try {
      const response = await patch({
        apiName: this.apiName,
        path,
        options: {
          headers: {
            "Content-Type": "application/json",
            // Authorization: `${await currentUserToken()}`,
            ...options.headers,
          },
          body: data,
          ...options,
        },
      });

      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error, "PATCH", path);
    }
  }

  /**
   * Makes a DELETE request to the API
   * @param {string} path - API endpoint path
   * @param {Object} options - Request options
   * @returns {Promise<Object>} API response
   */
  async delete(path, options = {}) {
    try {
      const response = await del({
        apiName: this.apiName,
        path,
        options: {
          headers: {
            "Content-Type": "application/json",
            // Authorization: `${await currentUserToken()}`,
            ...options.headers,
          },
          ...options,
        },
      });

      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error, "DELETE", path);
    }
  }

  /**
   * Handles successful API responses
   * @param {*} response - Raw API response
   * @returns {Object} Processed response
   */
  async handleResponse(response) {
    console.log("handleResponse response: ", response);
    const { statusCode, body } = await response;
    console.log(statusCode, body);
    // If response is already an object, return as is
    if (typeof response === "object" && response !== null) {
      return {
        success: true,
        data: response,
        timestamp: new Date().toISOString(),
      };
    }

    // Handle string responses
    if (typeof response === "string") {
      try {
        const parsed = JSON.parse(response);
        return {
          success: true,
          data: parsed,
          timestamp: new Date().toISOString(),
        };
      } catch (parseError) {
        return {
          success: true,
          data: response,
          timestamp: new Date().toISOString(),
        };
      }
    }

    // Default response format
    return {
      success: true,
      data: response,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Handles API errors
   * @param {Error} error - Error object
   * @param {string} method - HTTP method
   * @param {string} path - API path
   * @returns {ApiError} Formatted error
   */
  handleError(error, method, path) {
    console.error(`API ${method} ${path} failed:`, error);

    // Extract error details
    const errorDetails = {
      method,
      path,
      timestamp: new Date().toISOString(),
      originalError: error,
    };

    // Handle different error types
    if (error.response) {
      // HTTP error response
      const { status, data } = error.response;
      return new ApiError(
        data?.message || `HTTP ${status} error`,
        status,
        errorDetails
      );
    }

    if (error.request) {
      // Network error
      return new ApiError(
        "Network error - please check your connection",
        0,
        errorDetails
      );
    }

    // General error
    return new ApiError(
      error.message || "An unexpected error occurred",
      500,
      errorDetails
    );
  }
}

/**
 * Custom API Error class
 */
export class ApiError extends Error {
  constructor(message, statusCode = 500, details = {}) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }

  /**
   * Checks if error is a network error
   * @returns {boolean} True if network error
   */
  isNetworkError() {
    return this.statusCode === 0;
  }

  /**
   * Checks if error is a client error (4xx)
   * @returns {boolean} True if client error
   */
  isClientError() {
    return this.statusCode >= 400 && this.statusCode < 500;
  }

  /**
   * Checks if error is a server error (5xx)
   * @returns {boolean} True if server error
   */
  isServerError() {
    return this.statusCode >= 500;
  }

  /**
   * Gets user-friendly error message
   * @returns {string} User-friendly message
   */
  getUserMessage() {
    if (this.isNetworkError()) {
      return "Please check your internet connection and try again.";
    }

    if (this.statusCode === 404) {
      return "The requested resource was not found.";
    }

    if (this.statusCode === 403) {
      return "You do not have permission to access this resource.";
    }

    if (this.statusCode === 401) {
      return "Please log in to access this resource.";
    }

    if (this.isServerError()) {
      return "A server error occurred. Please try again later.";
    }

    return this.message;
  }
}

// Create and export default API service instance
export const apiService = new ApiService();

export default apiService;
